import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Coins, ArrowDown, ArrowUp, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { EDI } from "@/constants/testIds";

export default function BusinessValue() {
    const [data, setData] = useState(null);
    useEffect(() => {
        api.get("/edi/summary").then((r) => setData(r.data)).catch(() => toast.error("Failed to load business value"));
    }, []);

    if (!data) return <div className="kicker text-slate-500">Loading business value…</div>;

    return (
        <div className="space-y-8" data-testid={EDI.businessValueRoot}>
            <div className="flex items-end justify-between gap-6 flex-wrap">
                <div>
                    <div className="kicker text-[var(--sgsb-gold)]">Enterprise Delivery Intelligence™</div>
                    <h1 className="mt-2 font-display font-black text-white text-4xl md:text-5xl tracking-tighter">
                        Business Value
                    </h1>
                    <p className="mt-2 text-slate-400 max-w-2xl font-body text-sm">
                        Measurable outcomes from converting engineering knowledge into a corporate asset —
                        speed, quality, cost avoidance and productivity.
                    </p>
                </div>
                <div className="flex items-center gap-2 text-[var(--sgsb-gold)] text-xs font-body">
                    <Sparkles className="h-4 w-4" strokeWidth={1.5} />
                    <span className="kicker text-[10px]">Portfolio · Feb 2026</span>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                    ["Estimated Financial Savings", "$32.76M", "Portfolio · YTD"],
                    ["Engineering Hours Saved", "218,400", "Equivalent to 105 FTEs"],
                    ["Rework Reduction", "-66.8%", "Portfolio-wide"],
                ].map(([k, v, s]) => (
                    <div key={k} className="glass rounded-xl p-6 relative overflow-hidden">
                        <div className="absolute top-0 left-0 h-[3px] w-16 bg-[var(--sgsb-gold)]" />
                        <div className="flex items-center gap-2 text-[var(--sgsb-gold)]">
                            <Coins className="h-4 w-4" strokeWidth={1.5} />
                            <span className="kicker text-[10px]">Outcome</span>
                        </div>
                        <div className="mt-2 kicker text-slate-400">{k}</div>
                        <div className="mt-2 font-display font-black text-white text-4xl tracking-tighter">{v}</div>
                        <div className="mt-1 text-xs text-slate-500 font-body">{s}</div>
                    </div>
                ))}
            </div>

            <div className="glass rounded-xl overflow-hidden">
                <div className="p-4 border-b border-slate-800/60">
                    <h3 className="font-display font-black text-white tracking-tight">Measurable Outcomes</h3>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm font-body">
                        <thead>
                            <tr className="text-slate-500 kicker text-[10px]">
                                <th className="text-left px-4 py-3">Metric</th>
                                <th className="text-left px-4 py-3">Baseline</th>
                                <th className="text-left px-4 py-3">Current</th>
                                <th className="text-right px-4 py-3">Change</th>
                                <th className="text-right px-4 py-3">Impact</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.business_value.map((r) => {
                                const positive = r.delta_pct !== null && r.delta_pct !== undefined && (
                                    (r.metric.toLowerCase().includes("reduction") && r.delta_pct < 0) ||
                                    (r.metric.toLowerCase().includes("increase") && r.delta_pct > 0) ||
                                    (r.metric.toLowerCase().includes("improvement") && r.delta_pct > 0) ||
                                    (r.metric.toLowerCase().includes("saved") && r.delta_pct !== 0)
                                );
                                const Arrow = r.delta_pct === null || r.delta_pct === undefined
                                    ? null
                                    : (r.delta_pct < 0 ? ArrowDown : ArrowUp);
                                const color = r.delta_pct === null || r.delta_pct === undefined
                                    ? "text-slate-400"
                                    : positive ? "text-emerald-400" : "text-red-400";
                                return (
                                    <tr key={r.metric} className="border-t border-slate-800/60 hover:bg-slate-900/40 transition-colors duration-150">
                                        <td className="px-4 py-3 text-slate-100">{r.metric}</td>
                                        <td className="px-4 py-3 text-slate-400">{r.baseline}</td>
                                        <td className="px-4 py-3 text-slate-200 font-medium">{r.current}</td>
                                        <td className={`px-4 py-3 text-right ${color}`}>
                                            {Arrow ? (
                                                <span className="inline-flex items-center gap-1 justify-end">
                                                    <Arrow className="h-3 w-3" strokeWidth={2} />
                                                    {Math.abs(r.delta_pct).toFixed(1)}%
                                                </span>
                                            ) : "—"}
                                        </td>
                                        <td className="px-4 py-3 text-right text-[var(--sgsb-gold)] font-display font-bold">{r.impact}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="glass rounded-xl p-6 relative overflow-hidden">
                <div
                    className="absolute inset-0 opacity-40 pointer-events-none"
                    style={{
                        background:
                            "radial-gradient(ellipse at 90% 10%, rgba(212,175,55,0.22), transparent 55%), radial-gradient(ellipse at 10% 90%, rgba(59,130,246,0.16), transparent 55%)",
                    }}
                />
                <div className="relative">
                    <div className="kicker text-[var(--sgsb-gold)]">Executive summary</div>
                    <h3 className="mt-2 font-display font-black text-white text-2xl tracking-tighter">
                        Why Enterprise Delivery Intelligence pays back.
                    </h3>
                    <ul className="mt-4 space-y-2 text-slate-200 font-body text-sm leading-relaxed">
                        <li className="flex gap-3"><span className="text-[var(--sgsb-gold)]">•</span> Faster tenders — 52.5% shorter bid preparation across the portfolio.</li>
                        <li className="flex gap-3"><span className="text-[var(--sgsb-gold)]">•</span> Cheaper engineering — 85% reduction in data sheet preparation time.</li>
                        <li className="flex gap-3"><span className="text-[var(--sgsb-gold)]">•</span> Fewer defects — 66.8% reduction in engineering rework.</li>
                        <li className="flex gap-3"><span className="text-[var(--sgsb-gold)]">•</span> Better procurement — 39.3% shorter procurement cycle.</li>
                        <li className="flex gap-3"><span className="text-[var(--sgsb-gold)]">•</span> Reusable IP — knowledge reuse up from 12% to 47.3% in 24 months.</li>
                        <li className="flex gap-3"><span className="text-[var(--sgsb-gold)]">•</span> Portfolio uplift — $32.76M in estimated financial savings and 218,400 engineering hours freed.</li>
                    </ul>
                </div>
            </div>
        </div>
    );
}
